import { Overlay } from '@angular/cdk/overlay';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { bootstrapApplication, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, TitleStrategy } from '@angular/router';
import { VIRTUAL_SCROLLER_DEFAULT_OPTIONS_FACTORY } from '@iharbeck/ngx-virtual-scroller';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { MissingTranslationHandler, MissingTranslationHandlerParams, provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { LoggerModule, TOKEN_LOGGER_SERVER_SERVICE } from 'ngx-logger';
import { catchError, of, throwError } from 'rxjs';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { MaterialImportsModule } from './app/material-imports.module';
import { applyPatch as applyCtrlClickPatch } from './app/monkey/router-link-delegate';
import { GlobalErrorHandlerService } from './app/services/error/global-error-handler/global-error-handler.service';
import { CustomNGXLoggerHttpService } from './app/services/loggers/implementation/custom-ngx-logger-http.service';
import { CustomTitleService } from './app/services/page-title/custom-title-service';
import { TitleValidatingAppTitleStrategy } from './app/services/page-title/validating-title-strategy';
import { NgxTranslateModule } from './app/translate.module';
import { initializeKeycloak } from './app/utility/keycloak';
import { i18nEnglish } from './assets/i18n/en';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

applyCtrlClickPatch();

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserAnimationsModule,
      AppRoutingModule,
      LoggerModule.forRoot(environment.ngxLoggerConfig.logging, {
        serverProvider: {
          provide: TOKEN_LOGGER_SERVER_SERVICE, useClass: CustomNGXLoggerHttpService
        }
      }),
      KeycloakAngularModule,
      NgxTranslateModule,
      MaterialImportsModule
    ),
    provideIonicAngular(),
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    provideAppInitializer(() => {
      const initializerFn = initializeKeycloak(inject(KeycloakService), inject(Router));
      return initializerFn();
    }),
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: {
      hideDelay: 0,
      showDelay: 0,
      touchendHideDelay: 1500,
      position: 'above'
    } as MatTooltipDefaultOptions },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', floatLabel: 'always' } as MatFormFieldDefaultOptions },
    { provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: { color: 'primary' } },
    { provide: MAT_RADIO_DEFAULT_OPTIONS, useValue: { color: 'primary' } },
    { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: { color: 'primary' } },
    { provide: MAT_SELECT_CONFIG, useValue: { disableOptionCentering: true, hideSingleSelectionIndicator: true } },
    { provide: MAT_CARD_CONFIG, useValue: { appearance: 'outlined' } },
    { provide: MAT_TABS_CONFIG, useValue: { stretchTabs: false } },
    { provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: {
      hideSingleSelectionIndicator: true,
      hideMultipleSelectionIndicator: true,
    }},
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.reposition(),
      deps: [Overlay]
    },
    {
      provide: 'virtual-scroller-default-options',
      useFactory: VIRTUAL_SCROLLER_DEFAULT_OPTIONS_FACTORY
    },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideTranslateService({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: () => ({
          getTranslation() {
            return of(i18nEnglish);
          },
        })
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useFactory: () => ({
          handle(params: MissingTranslationHandlerParams) {
            const keyParts = params.key.split('.');
            if (keyParts[keyParts.length - 1] === '_unknown_') {
              return throwError(() => 'not found');
            }
            keyParts[keyParts.length - 1] = '_unknown_';
            const unknownKey = keyParts.join('.');
            const translation = params.translateService.get(unknownKey, params.interpolateParams)
              .pipe(catchError(() => of(params.key)));
            return translation;
          }
        })
      }
    }),
    {
      provide: Title,
      useClass: CustomTitleService
    },
    {
      provide: TitleStrategy,
      useClass: TitleValidatingAppTitleStrategy,
    }
  ]
});
